import type { AppConfig } from "../config";

export type ApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string; details?: unknown; service?: string };

/** A failure from any fetch; `status` 0 means it never reached the API. */
export type ApiFailure = Extract<ApiResult<unknown>, { ok: false }>;

export async function apiFetch<T>(
  cfg: AppConfig,
  token: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<ApiResult<T>> {
  // The service is the path's first segment (/content/…, /billing/…): the error state
  // prints it so a report says which one failed.
  const service = path.split("/")[1] || undefined;
  let res: Response;
  try {
    res = await fetch(`${cfg.apiUrl}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(body !== undefined ? { "content-type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // Offline, DNS, CORS: fetch rejects. A result keeps every caller's `r.ok` check whole.
    return { ok: false, status: 0, error: "network", service };
  }
  if (res.status === 204) return { ok: true, status: 204, data: undefined as T };
  const text = await res.text();
  let parsed: unknown = undefined;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    parsed = undefined;
  }
  if (res.ok) return { ok: true, status: res.status, data: parsed as T };
  const error =
    typeof parsed === "object" && parsed && "error" in parsed
      ? String((parsed as { error: unknown }).error)
      : `http_${res.status}`;
  return { ok: false, status: res.status, error, details: parsed, service };
}
